import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LocalStorageService } from './local-storage.service';
import { SharedServiceService } from './shared-service.service';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Dirty } from '../interface/dirty-interface';
import { Observable, of, from } from 'rxjs';
import { DialogComponent } from '../dialog/dialog.component';
import { map, switchMap } from 'rxjs/operators';
import { UnSavedChangesServiceService } from './un-saved-changes-service.service';
import { ConfigurationLockService } from './configuration-lock.service';

@Injectable({
  providedIn: 'root'
})
export class CandeactivateTcService {

  constructor(
    private dialog: MatDialog,
    private localStorage: LocalStorageService,
    private sharedService: SharedServiceService,
    private unSavedChangeService: UnSavedChangesServiceService,
    private configurationLockService: ConfigurationLockService,
    private router: Router
  ) { }

  canDeactivate(component: Dirty, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot)
    : Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // Check both parent and child unsaved changes
    const hasParentChanges = component.canDeactivate();
    const hasCommentChanges = component.canDeactivateComment ? component.canDeactivateComment() : false;

    // when user navigate to create project page
    if (this.localStorage.get("projectId") == null) {
      if (nextState.url == "/home" && (hasParentChanges || hasCommentChanges)) {
        // Save parent first without asking, then ask for comment
        return this.saveParentThenAskForComment(component, hasParentChanges, hasCommentChanges);
      }
      else if ((nextState.url == "/home" || nextState.url == '/tc-manageProject'
        || nextState.url == '/tc-configuration' || nextState.url == '/tc-eq')
       && (!hasParentChanges && !hasCommentChanges)) {
        return true;
      }
      // else if (nextState.url == '/tc-manageProject' && !hasParentChanges) {
      //   return true;
      // }
      // else if (nextState.url == '/tc-configuration' && !hasParentChanges) {
      //   return true;
      // }
      // else if (nextState.url == '/tc-eq' && !hasParentChanges) {
      //   return true;
      // }
      else if (nextState.url == '/tc-manageProject' && hasParentChanges) {
        return this.saveParentThenAskForComment(component, hasParentChanges, hasCommentChanges);
      }
      else if ((nextState.url == '/tc-cs' || nextState.url == '/tc-scm' 
        || nextState.url == '/tc-localcosts' || nextState.url == '/tc-investmentplan' || nextState.url == '/plc-tc-directcost') 
        && hasCommentChanges && !hasParentChanges) {
        return this.askAndSaveComment(component);
      }
      else {
        return this.saveParentThenAskForComment(component, hasParentChanges, hasCommentChanges);
      }
    }
    else if ((hasParentChanges || hasCommentChanges)) {
      if (currentState.url == "/tc-configuration-vblo") {
        return true;
      }
      if (currentState.url == "/tc-configuration" && nextState.url != "/tc-configuration-vblo") {
        this.localStorage.remove("configuration");
        this.localStorage.set("ConfigId", null);
        this.sharedService.changeConfigId(0);
      }

      const lockStatus = this.configurationLockService.getCurrentLockStatus();
      const shouldUnlock = nextState.url == "/home" ||
        nextState.url == "/tc-manageProject" ||
        nextState.url == "/tc-configuration" ||
        nextState.url == "/tc-project";

      if (shouldUnlock && lockStatus && lockStatus.isLocked && lockStatus.lockedByUserId === this.getUserInfo(component)) {

        return this.saveParentThenAskForComment(component, hasParentChanges, hasCommentChanges).pipe(
          switchMap(result => {
            this.configurationLockService.unlockConfiguration(
              this.getConfigurationId(component),
              this.localStorage.get("projectId"),
              this.getUserInfo(component)
            );
            return of(result);
          })
        );
      }

      return this.saveParentThenAskForComment(component, hasParentChanges, hasCommentChanges);
    }
    else {
      if (currentState.url == "/tc-configuration-vblo") {
        this.localStorage.remove("configuration")
      }
      const lockStatus = this.configurationLockService.getCurrentLockStatus();
      const shouldUnlock = (nextState.url == "/home" ||
        nextState.url == "/tc-manageProject" ||
        nextState.url == "/tc-configuration" ||
        nextState.url == "/tc-project") ;
      if (shouldUnlock && lockStatus && lockStatus.isLocked && lockStatus.lockedByUserId === this.getUserInfo(component)) {
        this.configurationLockService.unlockConfiguration(
          this.getConfigurationId(component),
          this.localStorage.get("projectId"),
          this.getUserInfo(component)
        );
      }
      return true;
    }
  }

  // Save parent automatically, then ask confirmation for comment
  private saveParentThenAskForComment(component: any, hasParentChanges: boolean | Observable<boolean>, hasCommentChanges: boolean): Observable<boolean> {
    // Step 1: Save parent changes automatically (no dialog)
    const saveParent$ = hasParentChanges
      ? from(component.submit())
      : of(true);

    return saveParent$.pipe(
      switchMap(parentSaved => {
        if (!parentSaved) {
          // Parent save failed, don't proceed
          return of(false);
        }

        // Step 2: If comment has changes, ask user for confirmation
        if (hasCommentChanges && component.submitComment) {
          return this.askAndSaveComment(component);
        }

        // No comment changes, allow navigation
        return of(true);
      })
    );
  }

  // Ask user for comment save confirmation
  private askAndSaveComment(component: any): Observable<boolean> {
    let dialogRef = this.dialog.open(DialogComponent, {
      data: {
        message: 'You have unsaved comments. Do you want to save them?',
        okBtn: "Yes",
        cancelBtn: "No"
      }
    });

    return dialogRef.afterClosed().pipe(
      switchMap(async result => {
        if (result) {
          // User wants to save comment
          const commentSaved = await component.submitComment();
          return commentSaved;
        } else {
          // User chose not to save comment, allow navigation anyway
          return true;
        }
      })
    );
  }

  getDeactivateStatus(component: any): Observable<boolean> {
    const result = component.submit();
    // Always return Observable<boolean>
    if (result instanceof Observable) {
      return result;
    }
    if (result instanceof Promise) {
      return from(result);
    }
    return of(result);
  }

  private getDeactivateStatusWithComment(component: any, hasCommentChanges: boolean): Observable<boolean> {
    if (!hasCommentChanges) {
      return of(true);
    }
    return this.askAndSaveComment(component);
  }

  getUserInfo(component: any) {
    return component.getUserInfo(component);
  }

  getConfigurationId(component: any) {
    return component.getConfigurationId(component);
  }
}
